/**
 * QrCodeVinculo — QR Code real com comunicação bidirecional em tempo real via SSE.
 *
 * Fluxo:
 *  1. POST /auth/qr-link            → sessionId
 *  2. fetchNetworkOrigin()          → IP real da rede local
 *  3. Gera QR com URL /security/bonding?s=sessionId usando IP de rede
 *  4. GET /auth/session-sse/:id     → SSE stream aberto (substitui polling)
 *  5. Mobile abre link              → backend emite MOBILE_CONNECTED → 🎉 celebração
 *  6. Mobile completa etapas        → SSE BIOMETRY_OK | GEO_OK | DONE
 *  7. Segundo celular               → SSE LOCKED → "link já utilizado"
 *  8. Auto-renova QR a cada 2 min  (gera nova sessão, SSE da antiga fecha)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { CheckCircle2, RefreshCw, Smartphone, ExternalLink, Lock, Wifi, Shield, Activity, Unplug, ShieldCheck, Fingerprint, Loader2, AlertTriangle } from 'lucide-react';
import { PushPermissionRequest } from '../security/PushPermissionRequest';
import { Modal } from '../ui/Modal';
import { clearPasskeyOnThisDevice, isPasskeyAvailableOnThisDevice, markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import { StyledQrCode } from '../../security/qr/StyledQrCode';
import {
  createDeviceBondingSession,
  desktopDisconnectDeviceBonding,
  getActiveDeviceBondingSession,
  getSecurityConfig,
} from '../../security/bonding/bondingSessionApi';
import {
  getPasskeyStatus,
  requestPasskeyRegistrationOptions,
  revokeMobileBondingPasskeyWithExplicitConsent,
  verifyPasskeyRegistration,
} from '../../security/passkey/passkeyApi';

const API = import.meta.env.VITE_API_URL || '/api/v1';
const SEQUENCIA_PASSOS = [
  { key: 'CONNECTED', label: 'Celular conectado' },
  { key: 'IDENTIFIED', label: 'Identidade verificada' },
  { key: 'BIOMETRY_OK', label: 'Biometria do celular validada' },
  { key: 'GEO_OK', label: 'Localização confirmada' },
  { key: 'DONE', label: 'Autenticação completa' },
];
const RENEW_MS = 2 * 60 * 1000;
const FLIP_REVEAL_DELAY = 3000;
const QR_SIZE = 340; // 260 * 1.3 = ~340
const EXPIRY_WARN_S = 3; // segundos antes do vencimento → QR pulsa

// Cores
const AMBER = '#fde68a';
const SCAN_RED = '#ef4444';

interface Props {
  onVinculado: () => void;
  onPular: () => void;
  somenteVinculo?: boolean;
  onPhaseChange?: (phase: Phase) => void;
  userEmail?: string;
  forcePasskeyEnrollment?: boolean;
}

type Phase =
  | 'loading'
  | 'qr'
  | 'connected'          // celular abriu — celebração
  | 'step_progress'      // etapas em andamento
  | 'locked'             // segundo celular tentou entrar
  | 'success'            // DONE — bonding completo
  | 'passkey_registering' // registrando passkey
  | 'push_permission'    // requisitando permissão de notificação (desktop)
  | 'passkey_done'       // passkey cadastrada e notificação ativada — countdown ativo
  | 'error';

interface StepInfo {
  step: string;
  label: string;
  done: boolean;
}

const STEP_LABELS: Record<string, string> = {
  CONNECTED:    'Celular conectado',
  IDENTIFIED:   'Identidade verificada',
  BIOMETRY_OK:  'Biometria validada',
  GEO_OK:       'Localização confirmada',
  LIVENESS_OK:  'Prova de vida OK',
  DONE:         'Autenticação completa',
};

function getHttpStatus(error: unknown): number | undefined {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : undefined;
}

function redirectExpiredSession(): void {
  localStorage.removeItem('ac_token');
  sessionStorage.removeItem('ac_token');
  window.location.href = '/login?expired=1';
}

function redirectOnUnauthorized(error: unknown): boolean {
  if (getHttpStatus(error) !== 401) return false;
  redirectExpiredSession();
  return true;
}

function unwrapPasskeyRegistrationOptions(optionsData: any): any {
  return optionsData?.options ?? optionsData;
}

async function fetchNetworkOrigin(): Promise<string> {
  // Em produção, o domínio público já é acessível pelo celular
  const hostname = window.location.hostname;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (!isLocal) return window.location.origin;

  // Em desenvolvimento local: buscar o IP da rede LAN para o celular acessar
  try {
    const res = await fetch(`${API}/auth/network-info`);
    if (!res.ok) throw new Error();
    const { ip } = await res.json();
    const { port, protocol } = window.location;
    return `${protocol}//${ip}${port ? `:${port}` : ''}`;
  } catch {
    return window.location.origin;
  }
}

export function QrCodeVinculo({ onVinculado, onPular, somenteVinculo, onPhaseChange, userEmail, forcePasskeyEnrollment }: Props) {
  const [phase, _setPhase]           = useState<Phase>('loading');
  const phaseRef = useRef<Phase>('loading');
  const setPhase = (p: Phase) => {
    phaseRef.current = p;
    _setPhase(p);
    if (onPhaseChange) {
      onPhaseChange(p);
    }
  };
  const [qrDataUrl, setQrDataUrl]   = useState<string>('');
  const [bondingUrl, setBondingUrl] = useState<string>('');
  const [sessionId, setSessionId]   = useState<string>('');
  const [flipSide, setFlipSide]     = useState<'qr' | 'info'>('info');
  const [countdown, setCountdown]   = useState(RENEW_MS / 1000);
  const [errorMsg, setErrorMsg]     = useState('');
  const [steps, setSteps]           = useState<StepInfo[]>([]);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [passkeyError, setPasskeyError] = useState('');
  const [doneCountdown, setDoneCountdown] = useState(5);
  const doneTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasNavigatedRef = useRef(false); // guard: impede onVinculado ser chamado 2x
  const hasAutoTriggeredPasskeyRef = useRef(false);

  useEffect(() => {
    if (forcePasskeyEnrollment && phase !== 'success' && phase !== 'passkey_registering' && phase !== 'push_permission' && phase !== 'passkey_done') {
      console.log('[QrCodeVinculo] Forçando transição para cadastro de passkey local (celular pulado).');
      setPhase('success');
    }
  }, [forcePasskeyEnrollment, phase]);
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.includes('ngrok-free.dev') || import.meta.env.DEV;

  const sseRef      = useRef<EventSource | null>(null);
  const renewRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flipRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const cdRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeRef    = useRef(true);
  const isFirstQrRef = useRef(true); // flip instrução só na primeira vez
  // Heartbeat: detecta celular inativo (fechou a página)
  const hbTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MOBILE_TIMEOUT_MS = 60_000; // 60s sem atividade → celular sumiu
  // Grace period: aguarda 45s antes de considerar desconexão real (tempo para usuário abrir PWA)
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const GRACE_MS = 45000;

  // ── Gera sessão + QR + abre SSE ──────────────────────────────────────────

  const gerarQr = useCallback(async () => {
    if (!activeRef.current) return;
    // Não gerar novo QR se já estamos em fases pós-bonding
    const p = phaseRef.current;
    if (p === 'success' || p === 'passkey_registering' || p === 'push_permission' || p === 'passkey_done') return;
    // Fechar SSE anterior se houver
    sseRef.current?.close();

    const isFirst = isFirstQrRef.current;
    // Só mostra loading na primeira vez — renovações são silenciosas
    if (isFirst) {
      setPhase('loading');
      setSteps([]);
    }

    try {
      const token = localStorage.getItem('ac_token') || '';
      const fp = [navigator.userAgent, screen.width, screen.height].join('|');

      const data = await createDeviceBondingSession(
        { fingerprint: fp },
        { bearerToken: token },
      );


      const networkOrigin = await fetchNetworkOrigin();
      const url = data.bondingUrl ?? `${networkOrigin}/security/bonding?s=${data.sessionId}`;

      if (!activeRef.current) return;
      setQrDataUrl(url);
      setBondingUrl(url);
      setSessionId(data.sessionId);
      setPhase('qr');
      setCountdown(RENEW_MS / 1000);

      // Abrir SSE
      openSSE(data.sessionId);

      // Auto-renovação do QR
      scheduleRenew();
      // Mostrar QR direto — sem flip de instrucao
      setFlipSide('qr');
      if (isFirst) {
        isFirstQrRef.current = false;
      }
      startCountdown();
    } catch (e: any) {
      if (redirectOnUnauthorized(e)) return;
      if (!activeRef.current) return;
      setErrorMsg(e.message || 'Erro ao gerar QR');
      setPhase('error');
    }
  }, []);

  // ── SSE + Polling fallback ─────────────────────────────────────────────────
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function openSSE(sid: string) {
    sseRef.current?.close();
    if (pollRef.current) clearInterval(pollRef.current);

    const es = new EventSource(`${API}/auth/session-sse/${sid}`, { withCredentials: true });
    sseRef.current = es;

    es.addEventListener('session_update', (e: MessageEvent) => {
      if (!activeRef.current) return;
      // Qualquer mensagem do celular reinicia o heartbeat timeout
      resetHeartbeat();
      try {
        const { status, step, ping } = JSON.parse(e.data) as { status: string; step?: string; ping?: boolean };
        handleSessionUpdate(status, step, ping);
      } catch { /* ignorar */ }
    });

    // Evento explícito de desconexão do celular (backend emite quando SSE mobile fecha)
    es.addEventListener('mobile_disconnected', () => {
      if (!activeRef.current) return;
      // NÃO age imediatamente — inicia grace period de 5s
      // Se mobile reconectar (session_update chegar), cancela
      startGracePeriod();
    });

    es.onerror = () => {
      // SSE desconectou — o browser tenta reconectar automaticamente
    };

    // Polling fallback: Cloud Run e outros proxies podem fechar SSE silenciosamente.
    // Verificar status da sessão a cada 15s para não depender apenas do SSE.
    pollRef.current = setInterval(async () => {
      if (!activeRef.current) return;
      try {
        const res = await fetch(`${API}/auth/link-status/${sid}`);
        if (!res.ok) return;
        const { status, currentStep } = await res.json();
        if (status && status !== 'PENDING') {
          resetHeartbeat(); // Polling tambem reseta heartbeat (SSE pode estar morto)
          handleSessionUpdate(status, currentStep);
        }
      } catch { /* silencioso */ }
    }, 15000);
  }

  /** Mobile não está mais respondendo — limpar sessão e gerar novo QR */
  function handleMobileDisconnect() {
    // Não resetar se já estamos em fases pós-bonding
    const p = phaseRef.current;
    if (p === 'success' || p === 'passkey_registering' || p === 'push_permission' || p === 'passkey_done') {
      clearHeartbeat();
      return;
    }
    clearHeartbeat();
    clearGrace();

    // Antes de resetar a sessão local e gerar um QR novo, verificar se o celular
    // já concluiu o bonding (o que eleva o trustLevel do usuário para >= 4)
    checkBondingAlreadyDone().then(done => {
      if (done) return;
      sseRef.current?.close();
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setSteps([]);
      setPhase('loading');
      setTimeout(() => { if (activeRef.current) gerarQr(); }, 500);
    });
  }

  /** Reinicia o timer de inatividade do celular.
   *  @param timeoutMs override para timeout (usado em resume com probe curto) */
  function resetHeartbeat(timeoutMs = MOBILE_TIMEOUT_MS) {
    clearHeartbeat();
    hbTimeoutRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      // Celular não enviou nada → provavelmente fechou a página
      handleMobileDisconnect();
    }, timeoutMs);
  }

  function clearHeartbeat() {
    if (hbTimeoutRef.current) { clearTimeout(hbTimeoutRef.current); hbTimeoutRef.current = null; }
  }

  /** Inicia grace period: espera antes de considerar desconexão real */
  function startGracePeriod() {
    // Não agir se já estamos em fases pós-bonding
    const p = phaseRef.current;
    if (p === 'success' || p === 'passkey_registering' || p === 'push_permission' || p === 'passkey_done') return;
    clearGrace();
    graceRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      // Verificar fase novamente — pode ter mudado durante o grace period
      const pp = phaseRef.current;
      if (pp === 'success' || pp === 'passkey_registering' || pp === 'push_permission' || pp === 'passkey_done') return;
      // Grace period expirou sem reconexão → desconexão real
      clearHeartbeat();
      sseRef.current?.close();
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setPhase('loading');
      setTimeout(() => { if (activeRef.current) gerarQr(); }, 500);
    }, GRACE_MS);
  }

  function clearGrace() {
    if (graceRef.current) { clearTimeout(graceRef.current); graceRef.current = null; }
  }

  function handleSessionUpdate(status: string, step?: string, ping?: boolean) {
    // Se já completou bonding, não processar mais eventos SSE
    const p = phaseRef.current;
    if (p === 'success' || p === 'passkey_registering' || p === 'passkey_done' || p === 'push_permission') return;

    // Qualquer update do mobile cancela grace period — mobile está ativo
    clearGrace();

    // Resetar heartbeat para QUALQUER update (não apenas MOBILE_CONNECTED)
    // Isso previne timeout durante steps intermediários (BIOMETRY_OK, GEO_OK, etc.)
    resetHeartbeat();

    // Pings: apenas resetar heartbeat, não alterar UI
    if (ping) return;

    // Verificação de sucesso / conclusão deve vir ANTES de qualquer outro status
    if (status === 'DONE' || status === 'APPROVED' || step === 'DONE') {
      addStep('DONE');
      sseRef.current?.close();
      if (pollRef.current) clearInterval(pollRef.current);
      clearHeartbeat();
      setPhase('success');
      // NÃO auto-avança — o usuário clica no botão 'Cadastrar Computador'
      return;
    }

    if (status === 'MOBILE_CONNECTED') {
      clearGrace();
      clearFlip();
      clearCd();
      clearTimeout(renewRef.current!);
      addStep('CONNECTED');
      // Se veio com step adicional (IDENTIFIED, etc.), registrar também
      if (step && step !== 'CONNECTED' && STEP_LABELS[step]) {
        addStep(step);
      }

      setPhase('step_progress');
      // Inicia heartbeat: se celular não interagir em 30s, considera desconectado
      resetHeartbeat();
      return;
    }

    if (status === 'LOCKED') {
      setPhase('locked');
      return;
    }

    // Etapas intermediárias
    if (step && STEP_LABELS[step]) {
      addStep(step);
      setPhase('step_progress');
    }
  }

  function addStep(step: string) {
    setSteps(prev => {
      if (prev.some(s => s.step === step)) return prev;
      return [...prev, { step, label: STEP_LABELS[step] ?? step, done: true }];
    });
  }

  // ── Timers ────────────────────────────────────────────────────────────────

  function scheduleRenew(delayMs = RENEW_MS) {
    clearTimeout(renewRef.current!);
    renewRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      clearCd();
      checkBondingAlreadyDone().then(done => {
        if (!done && activeRef.current) gerarQr();
      });
    }, delayMs);
  }

  /** Mostra instrução primeiro, depois revela QR UMA VEZ e para. */
  function startFlip() {
    clearTimeout(flipRef.current!);
    setFlipSide('info');
    // Após 3s, revela o QR definitivamente — sem mais flips
    flipRef.current = setTimeout(() => {
      if (!activeRef.current) return;
      setFlipSide('qr');
    }, FLIP_REVEAL_DELAY) as any;
  }

  function startCountdown() {
    clearInterval(cdRef.current!);
    cdRef.current = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(cdRef.current!); return 0; } return c - 1; });
    }, 1000);
  }

  function clearFlip() { clearTimeout(flipRef.current!); }
  function clearCd()   { clearInterval(cdRef.current!); }

  /** Verifica se já existe sessão ativa para o usuário (desktop recarregou) */
  const checkActiveSession = async (): Promise<boolean> => {
    const token = localStorage.getItem('ac_token') || '';
    if (!token) return false;
    try {
      const data = await getActiveDeviceBondingSession({ bearerToken: token });
      if (!data?.sessionId) return false;
      const activeSessionId = data.sessionId;
      const activeStatus = data.status ?? 'PENDING';

      // Se a sessão está no status PENDING (mobile nunca conectou), vamos retomar
      // apenas se ainda restarem mais de 10 segundos na sessão atual.
      if (activeStatus === 'PENDING') {
        if (data.expiraEm) {
          const restSec = Math.floor((new Date(data.expiraEm).getTime() - Date.now()) / 1000);
          if (restSec <= 10) {
            return false;
          }
        } else {
          return false;
        }
      }

      // Sessão ativa encontrada — reconectar SSE nela
      setSessionId(activeSessionId);

      // Restaurar fase visual baseada no status atual
      const statusToPhase: Record<string, Phase> = {
        PENDING:          'qr',
        MOBILE_CONNECTED: 'step_progress',
        BIOMETRY_OK:      'step_progress',
        GEO_OK:           'step_progress',
        LIVENESS_OK:      'step_progress',
        DONE:             'success',
        APPROVED:         'success',
      };
      const resumePhase = (statusToPhase[activeStatus] ?? 'qr') as Phase;

      // Se mobile já está conectado, restaurar steps já feitos
      if (data.currentStep && data.currentStep !== 'PENDING') {
        const doneSteps = ['CONNECTED', 'BIOMETRY_OK', 'GEO_OK', 'LIVENESS_OK', 'DONE'];
        const idx = doneSteps.indexOf(data.currentStep);
        const completed = idx >= 0 ? doneSteps.slice(0, idx + 1) : ['CONNECTED'];
        setSteps(completed.map(s => ({ step: s, label: STEP_LABELS[s] ?? s, done: true })));
      }

      setPhase(resumePhase);

      // Se sessão já está APPROVED, não precisa reconectar SSE nem heartbeat
      if (resumePhase === 'success') {
        return true;
      }

      // Reconectar SSE para continuar recebendo eventos do mobile
      openSSE(activeSessionId);

      // Se status indica mobile conectado, iniciar heartbeat com probe curto.
      // Se o mobile já desconectou (fechou), o timeout de 5s vai detectar e gerar QR novo.
      // Usamos 5s (não 30s) porque estamos retomando — se o mobile estiver vivo,
      // ele envia ping a cada 10s, então 5s é suficiente para validar.
      if (['MOBILE_CONNECTED', 'BIOMETRY_OK', 'GEO_OK', 'LIVENESS_OK'].includes(activeStatus)) {
        resetHeartbeat(5_000);
      }

      // Se PENDING, precisa gerar o QR visual para este sessionId
      if (resumePhase === 'qr') {
        const networkOrigin = await fetchNetworkOrigin();
        const url = `${networkOrigin}/security/bonding?s=${activeSessionId}`;
        setQrDataUrl(url);
        setBondingUrl(url);
        // Não chamar startFlip — é resume, o QR já aparece direto
        setFlipSide('qr');
        
        let delayMs = RENEW_MS;
        if (data.expiraEm) {
          const restSec = Math.floor((new Date(data.expiraEm).getTime() - Date.now()) / 1000);
          const activeSec = Math.max(0, restSec);
          setCountdown(activeSec);
          delayMs = activeSec * 1000;
        } else {
          startCountdown();
        }
        scheduleRenew(delayMs);
      }

      return true;
    } catch (err) {
      if (redirectOnUnauthorized(err)) return false;
      return false;
    }
  };
  /** Verifica se o usuário já tem Passkey cadastrada — se sim, pula direto para passkey_done */
  const checkPasskeyAlreadyDone = async (): Promise<boolean> => {
    const token = localStorage.getItem('ac_token') || '';
    if (!token) return false;
    try {
      const data = await getPasskeyStatus();
      const emailToUse = (userEmail || '').trim().toLowerCase();
      const hasLocalKey = emailToUse ? isPasskeyAvailableOnThisDevice(emailToUse) : false;

      // Só pula o processo se a passkey existir no servidor E estiver ativa localmente neste navegador específico
      if (data.temPasskeyDesktop && hasLocalKey) {
        startFinalCountdown();
        return true;
      }
      if (emailToUse && !data.temPasskeyDesktop) {
        clearPasskeyOnThisDevice(emailToUse);
      }
      return false;
    } catch (err) {
      if (redirectOnUnauthorized(err)) return false;
      return false;
    }
  };

  /** Verifica se o bonding mobile já foi concluído (trustLevel >= 4) — se sim, pula QR e vai direto para tela de cadastrar desktop */
  const checkBondingAlreadyDone = async (): Promise<boolean> => {
    const token = localStorage.getItem('ac_token') || '';
    if (!token) return false;
    try {
      // Buscar trustLevel do usuário no backend
      const data = await getSecurityConfig({ bearerToken: token });
      if ((data.trustLevel ?? 0) >= 4) {
        // Evitar que o useEffect de sucesso auto-dispare o popup da passkey local ao carregar a página
        hasAutoTriggeredPasskeyRef.current = true;

        // Bonding mobile já completado — exibir direto o botão de cadastrar passkey desktop
        setSteps([
          { step: 'CONNECTED', label: STEP_LABELS.CONNECTED, done: true },
          { step: 'BIOMETRY_OK', label: STEP_LABELS.BIOMETRY_OK, done: true },
          { step: 'GEO_OK', label: STEP_LABELS.GEO_OK, done: true },
          { step: 'DONE', label: STEP_LABELS.DONE, done: true },
        ]);
        setPhase('success');
        return true;
      }
      return false;
    } catch (err) {
      if (redirectOnUnauthorized(err)) return false;
      return false;
    }
  };

  useEffect(() => {
    activeRef.current = true;
    // 1. Verificar se passkey já existe (flow completo anterior)
    // 2. Verificar se bonding mobile já foi concluído (trustLevel >= 4)
    // 3. Verificar sessão ativa em Redis (bonding em andamento)
    // 4. Se nenhum, gerar QR novo
    if (somenteVinculo) {
      // Se for somente vínculo de celular, ignoramos a verificação de passkey/bonding do desktop
      // para forçar a exibição do QR Code e permitir o pareamento do novo celular
      checkActiveSession().then(hasActive => {
        if (!hasActive && activeRef.current) gerarQr();
      });
    } else {
      checkPasskeyAlreadyDone().then(hasPk => {
        if (hasPk) return;
        checkBondingAlreadyDone().then(hasBonding => {
          if (hasBonding) return;
          checkActiveSession().then(hasActive => {
            if (!hasActive && activeRef.current) gerarQr();
          });
        });
      });
    }
    return () => {
      activeRef.current = false;
      sseRef.current?.close();
      if (pollRef.current) clearInterval(pollRef.current);
      clearFlip(); clearCd();
      clearTimeout(renewRef.current!);
      clearHeartbeat();
      clearGrace();
      if (doneTimerRef.current) clearInterval(doneTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-aciona o registro da passkey no desktop ao entrar na fase de sucesso
  useEffect(() => {
    if (phase === 'success' && !hasAutoTriggeredPasskeyRef.current) {
      if (somenteVinculo) {
        // Se for somente vínculo de celular, não fazemos o registro de passkey do desktop
        return;
      }
      hasAutoTriggeredPasskeyRef.current = true;
      handlePasskeyRegister();
    }
  }, [phase, somenteVinculo]);

  // ── Render helpers ────────────────────────────────────────────────────────

  const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  /** Desktop desconecta o celular e volta ao QR */
  const handleDesktopDisconnect = async () => {
    setShowDisconnectConfirm(false);
    try {
      const token = localStorage.getItem('ac_token') || '';
      await desktopDisconnectDeviceBonding(sessionId, { bearerToken: token });
    } catch (err) {
      if (redirectOnUnauthorized(err)) return;
    }
    // Limpar tudo e voltar ao QR
    hasAutoTriggeredPasskeyRef.current = false;
    sseRef.current?.close();
    clearFlip(); clearCd();
    clearTimeout(renewRef.current!);
    clearHeartbeat(); clearGrace();
    setSteps([]);
    setSessionId('');
    setQrDataUrl('');
    setPhase('loading');
    isFirstQrRef.current = false; // não mostrar flip de instrução de novo
    gerarQr();
  };

  /** Link sutil de desconectar + card de confirmação */
  const renderDisconnectLink = () => (
    <>
      {/* Overlay modal com fundo escurecido */}
      {showDisconnectConfirm && (
        <div
          onClick={() => setShowDisconnectConfirm(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'sw-fade-in .2s ease',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 340, padding: '28px 24px', borderRadius: 20,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 24px 48px rgba(0,0,0,.15)',
              textAlign: 'center',
              animation: 'sw-pop .3s cubic-bezier(.175,.885,.32,1.275)',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(239,68,68,.1)', border: '2px solid rgba(239,68,68,.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <Unplug size={26} style={{ color: '#f87171' }} />
            </div>
            <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>
              Desconectar celular?
            </h3>
            <p style={{ fontSize: '.85rem', color: '#64748b', margin: '0 0 20px', lineHeight: 1.5 }}>
              O celular conectado será desvinculado e você precisará escanear o QR Code novamente.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                style={{
                  padding: '10px 22px', borderRadius: 10,
                  background: '#f8fafc', border: '1px solid #e2e8f0',
                  color: '#475569', cursor: 'pointer', fontSize: '.85rem', fontWeight: 600,
                  transition: 'all .2s',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDesktopDisconnect}
                style={{
                  padding: '10px 22px', borderRadius: 10,
                  background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.2)',
                  color: '#ef4444', cursor: 'pointer', fontSize: '.85rem', fontWeight: 700,
                  transition: 'all .2s',
                }}
              >
                Desconectar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Link sutil */}
      <button
        onClick={() => setShowDisconnectConfirm(true)}
        style={{
          marginTop: 14, background: 'none', border: 'none', cursor: 'pointer',
          color: '#334155', fontSize: '.7rem', fontWeight: 500, padding: '4px 8px',
          display: 'flex', alignItems: 'center', gap: 5, alignSelf: 'center',
          transition: 'color .2s',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = '#94a3b8')}
        onMouseLeave={e => (e.currentTarget.style.color = '#334155')}
      >
        <Unplug size={11} /> Desconectar celular
      </button>
    </>
  );

  // ── Phase: loading ────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <div style={{ width: 44, height: 44, border: '3px solid rgba(251,191,36,.2)', borderTopColor: '#fde68a', borderRadius: '50%', animation: 'sw-spin .8s linear infinite' }} />
      </div>
    );
  }

  // ── Phase: error ─────────────────────────────────────────────────────────
  if (phase === 'error') {
    const isDesativado = errorMsg.toLowerCase().includes('desativado');
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: 24 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,.12)', border: '2px solid rgba(239,68,68,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertTriangle size={30} style={{ color: '#f87171' }} />
        </div>
        <p style={{ color: '#ef4444', fontSize: '.85rem', textAlign: 'center', fontWeight: 600 }}>{errorMsg}</p>
        {!isDesativado && (
          <button onClick={gerarQr} style={{ padding: '8px 20px', borderRadius: 10, background: 'rgba(251,191,36,.15)', border: '1px solid #fde68a', color: '#fde68a', cursor: 'pointer', fontWeight: 600, fontSize: '.82rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} /> Tentar novamente
          </button>
        )}
      </div>
    );
  }

  // ── Phase: locked ─────────────────────────────────────────────────────────
  if (phase === 'locked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 28, textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,.12)', border: '2px solid rgba(239,68,68,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Lock size={30} style={{ color: '#f87171' }} />
        </div>
        <p style={{ color: '#0f172a', fontWeight: 800, fontSize: '1rem', margin: 0 }}>Link já utilizado</p>
        <p style={{ color: '#64748b', fontSize: '.85rem', margin: 0, maxWidth: 200, lineHeight: 1.5 }}>
          Este QR Code já está em uso em outro dispositivo.
        </p>
        <button onClick={gerarQr} style={{ marginTop: 8, padding: '8px 18px', borderRadius: 10, background: 'rgba(251,191,36,.12)', border: '1px solid rgba(251,191,36,.3)', color: '#fde68a', cursor: 'pointer', fontWeight: 600, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={12} /> Gerar novo QR Code
        </button>
      </div>
    );
  }

  // ── Phase: connected ─────────────────────────────────────────────────────
  // ── Phase: connected e step_progress (Layout Unificado) ───────────────────
  if (phase === 'connected' || phase === 'step_progress') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 16px', minWidth: 280 }}>
        {/* Animação do celular conectado no topo */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
          <div style={{ position: 'relative', width: 64, height: 64, margin: '0 auto' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: 'rgba(16,185,129,.12)', border: '2px solid #10b981',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              animation: 'sw-pop .5s cubic-bezier(.175,.885,.32,1.275)'
            }}>
              <Smartphone size={28} style={{ color: '#10b981' }} />
            </div>
            {/* Ondas de sinal menores */}
            {[1, 2].map(i => (
              <div
                key={i}
                style={{
                  position: 'absolute', inset: -i * 8, borderRadius: '50%',
                  border: `1px solid rgba(16,185,129,${0.25 - i * 0.08})`,
                  animation: `sw-ripple ${1.2 + i * 0.3}s ease-out ${i * 0.25}s infinite`
                }}
              />
            ))}
          </div>
          <div>
            <h4 style={{ color: '#10b981', fontWeight: 800, fontSize: '0.95rem', margin: '0 0 2px' }}>
              Celular Conectado
            </h4>
            <p style={{ color: '#94a3b8', fontSize: '.75rem', margin: 0 }}>
              Siga os passos indicados na tela do seu celular
            </p>
          </div>
        </div>

        {/* Linha divisória sutil */}
        <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0' }} />

        {/* Lista fixa das etapas da sequência */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SEQUENCIA_PASSOS.map((passo, idx) => {
            // Determinar status do passo
            const concluido = steps.some(s => s.step === passo.key);
            
            // Um passo está em andamento se ele não está concluído, e (ou é o primeiro passo ou o passo anterior está concluído)
            const anteriorConcluido = idx > 0 && steps.some(s => s.step === SEQUENCIA_PASSOS[idx - 1].key);
            const emAndamento = !concluido && (idx === 0 || anteriorConcluido);
            
            const pendente = !concluido && !emAndamento;

            return (
              <div
                key={passo.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 10,
                  transition: 'all .3s ease',
                  ...(concluido && {
                    background: 'rgba(16,185,129,.06)',
                    border: '1px solid rgba(16,185,129,.15)',
                  }),
                  ...(emAndamento && {
                    background: 'rgba(245,158,11,.06)',
                    border: '1px dashed rgba(245,158,11,.25)',
                    animation: 'sw-pulse-border 2s infinite',
                  }),
                  ...(pendente && {
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    opacity: 0.7,
                  }),
                }}
              >
                {concluido && (
                  <CheckCircle2 size={15} style={{ color: '#10b981', flexShrink: 0 }} />
                )}
                {emAndamento && (
                  <Loader2 size={15} style={{ color: '#f59e0b', animation: 'sw-spin .8s linear infinite', flexShrink: 0 }} />
                )}
                {pendente && (
                  <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #94a3b8', flexShrink: 0 }} />
                )}

                <span style={{
                  fontSize: '.85rem',
                  fontWeight: concluido || emAndamento ? 700 : 500,
                  color: concluido ? '#059669' : emAndamento ? '#d97706' : '#94a3b8',
                }}>
                  {passo.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Linha divisória inferior sutil */}
        <div style={{ height: 1, background: '#e2e8f0', margin: '4px 0 0' }} />

        {/* Desconectar */}
        {renderDisconnectLink()}
      </div>
    );
  }

  // ── Phase: success ────────────────────────────────────────────────────────

  /** Inicia o registro de Passkey (WebAuthn) */
  async function handlePasskeyRegister() {
    setPasskeyError('');
    setPhase('passkey_registering');
    try {
      // 1. Buscar challenge do servidor
      const optionsData = await requestPasskeyRegistrationOptions({
        nomeDispositivo: 'Estação Principal',
      });
      const options = unwrapPasskeyRegistrationOptions(optionsData);

      // 2. Acionar biometria do dispositivo (Windows Hello / Touch ID)
      const attResp = await startRegistration({ optionsJSON: options });

      // 3. Enviar resposta para o backend validar e persistir
      const result = await verifyPasskeyRegistration(attResp, {
        nomeDispositivo: 'Estação Principal',
      });
      const resultMessage =
        typeof result.message === 'string' ? result.message : 'Verificação falhou';
      if (!result.ok) throw new Error(resultMessage);

      // 4. Sucesso - transição para solicitar notificação Push e salvar na sessão que já destravou com biometria
      sessionStorage.setItem('ac_pwa_unlocked_this_session', 'true');

      // Salvar a flag local de passkey neste navegador
      const emailToUse = (userEmail || '').trim().toLowerCase();
      if (emailToUse) {
        try {
          markPasskeyOnThisDevice(emailToUse);
          console.log('[QrCodeVinculo] Passkey local gravada no localStorage para:', emailToUse);
        } catch (err) {
          console.error('[QrCodeVinculo] Erro ao gravar passkey no localStorage:', err);
        }
      }

      setPhase('push_permission');
    } catch (e: any) {
      if (redirectOnUnauthorized(e)) return;
      // Se o usuário cancelou a biometria ou houve erro
      const msg = e?.name === 'NotAllowedError'
        ? 'Registro cancelado. Tente novamente.'
        : (e?.message || 'Erro ao registrar Passkey');
      setPasskeyError(msg);
      setPhase('success'); // volta ao card de sucesso
    }
  }

  function startFinalCountdown() {
    if (doneTimerRef.current) {
      clearInterval(doneTimerRef.current);
      doneTimerRef.current = null;
    }

    setPhase('passkey_done');
    setDoneCountdown(5);
    hasNavigatedRef.current = false;
    doneTimerRef.current = setInterval(() => {
        setDoneCountdown(prev => {
          if (prev <= 1) {
            clearInterval(doneTimerRef.current!);
            doneTimerRef.current = null;
            // Chamar onVinculado FORA do updater para evitar double-fire do StrictMode
            setTimeout(() => {
              if (!hasNavigatedRef.current) {
                hasNavigatedRef.current = true;
                onVinculado();
              }
            }, 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
  }

  /** Aciona a exibição do modal personalizado de confirmação de desvinculação */
  const handleResetBonding = () => {
    setShowResetConfirm(true);
  };

  /** Executa o desvínculo real do celular no backend */
  const handleResetBondingReal = async () => {
    try {
      await revokeMobileBondingPasskeyWithExplicitConsent({
        explicitConsent: true,
        reason: 'qr-bonding-user-confirmed-reset',
      });

      // Sucesso — redefinir todo o estado e gerar um novo QR Code
      hasAutoTriggeredPasskeyRef.current = false;
      sseRef.current?.close();
      clearFlip(); clearCd();
      clearTimeout(renewRef.current!);
      clearHeartbeat(); clearGrace();
      setSteps([]);
      setSessionId('');
      setQrDataUrl('');
      setPhase('loading');
      isFirstQrRef.current = false;
      gerarQr();
    } catch (err: any) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('ac_token');
        sessionStorage.removeItem('ac_token');
        window.location.href = '/login?expired=1';
        return;
      }
      setPasskeyError(
        err?.response
          ? 'Falha ao desvincular o celular. Tente novamente.'
          : 'Erro de conexão ao tentar desvincular o celular.',
      );
    }
  };

  if (phase === 'success') {
    return (
      <>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
          padding: '24px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden',
          borderRadius: 20, background: '#f8fafc',
          border: '1px solid rgba(108, 92, 231, 0.3)',
        }}>
          {/* Ambient purple glow */}
          <div style={{
            position: 'absolute', top: -40, left: '50%', transform: 'translateX(-50%)',
            width: 200, height: 200, background: 'rgba(108, 92, 231, 0.08)',
            filter: 'blur(50px)', zIndex: 0,
          }} />

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            {/* Animated ShieldCheck — same style as mobile DONE */}
            <div style={{
              width: 96, height: 96, borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(108, 92, 231, .1), rgba(90, 79, 207, .05))',
              border: '2px solid rgba(108, 92, 231, 0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 30px rgba(108, 92, 231, 0.1), inset 0 0 15px rgba(108, 92, 231, 0.05)',
              animation: 'sw-success-pulse 2s ease-in-out infinite',
            }}>
              <ShieldCheck size={48} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
            </div>

            <div>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' }}>
                Parabéns!
              </h2>
              <p style={{ color: '#6c5ce7', fontWeight: 700, fontSize: '.95rem', margin: '0 0 4px' }}>
                Vinculado com <span style={{ color: '#8b5cf6' }}>Sucesso</span>
              </p>
              <p style={{ color: '#64748b', fontSize: '.85rem', margin: 0, maxWidth: 220, lineHeight: 1.5 }}>
                {somenteVinculo
                  ? 'Seu dispositivo móvel foi conectado à sua conta com sucesso.'
                  : 'Sua identidade foi confirmada. Registre este computador para acesso seguro permanente.'}
              </p>
            </div>

            {passkeyError && (
              <div style={{
                padding: '8px 16px', borderRadius: 10,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                fontSize: '.78rem', color: '#f87171', maxWidth: 260,
              }}>
                {passkeyError}
              </div>
            )}

            <button
              onClick={somenteVinculo ? onVinculado : handlePasskeyRegister}
              style={{
                marginTop: 4, padding: '12px 28px', borderRadius: 14, border: 'none',
                background: 'linear-gradient(135deg, #6c5ce7, #5a4fcf)',
                color: '#fff', fontSize: '.92rem', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
                boxShadow: '0 4px 14px rgba(108, 92, 231, 0.25)',
                transition: 'all .3s cubic-bezier(.16,1,.3,1)',
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.97)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {somenteVinculo ? <CheckCircle2 size={16} /> : <Fingerprint size={16} />}
              {somenteVinculo ? 'Concluir' : 'Cadastrar Computador'}
            </button>

            {/* Ocultado temporariamente a pedido do usuário
            {!somenteVinculo && (
              <button
                onClick={handleResetBonding}
                style={{
                  marginTop: 14,
                  padding: '10px 18px',
                  borderRadius: 12,
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  background: 'rgba(148, 163, 184, 0.05)',
                  color: '#475569',
                  fontSize: '.78rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.2s ease',
                  width: '100%',
                  maxWidth: 260,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(148, 163, 184, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.25)';
                  e.currentTarget.style.color = '#475569';
                }}
                onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
                onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Smartphone size={15} />
                <span>Celular desinstalado? Parear novamente</span>
              </button>
            )}
            */}
          </div>

          <style>{`
            @keyframes sw-success-pulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 30px rgba(108, 92, 231, 0.2), inset 0 0 15px rgba(108, 92, 231, 0.1); }
              50% { transform: scale(1.05); box-shadow: 0 0 40px rgba(108, 92, 231, 0.35), inset 0 0 20px rgba(108, 92, 231, 0.15); }
            }
          `}</style>
        </div>

        {/* Modal de confirmação personalizado orientando e explicando detalhadamente */}
        <Modal
          open={showResetConfirm}
          onClose={() => setShowResetConfirm(false)}
          title="Desvincular Dispositivo Móvel"
          footer={
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', width: '100%' }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowResetConfirm(false)}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: '#fff', color: '#475569', fontSize: '.85rem', fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-danger-solid"
                onClick={() => {
                  setShowResetConfirm(false);
                  handleResetBondingReal();
                }}
                style={{
                  padding: '10px 20px', borderRadius: 10, border: 'none',
                  background: '#dc2626', color: '#fff', fontSize: '.85rem', fontWeight: 600,
                  cursor: 'pointer', boxShadow: '0 2px 6px rgba(220, 38, 38, 0.2)'
                }}
              >
                Sim, Desvincular
              </button>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{
                flexShrink: 0, width: 44, height: 44, borderRadius: 10,
                background: 'rgba(239, 68, 68, 0.1)', display: 'flex',
                alignItems: 'center', justifyContent: 'center'
              }}>
                <AlertTriangle size={22} color="#dc2626" strokeWidth={2.2} />
              </div>
              <div>
                <h4 style={{ margin: '0 0 4px', fontSize: '.95rem', fontWeight: 700, color: '#0f172a' }}>
                  Atenção: Ação Irreversível
                </h4>
                <p style={{ margin: 0, fontSize: '.85rem', color: '#475569', lineHeight: 1.5 }}>
                  Esta ação removerá permanentemente o vínculo com o celular atual.
                </p>
              </div>
            </div>

            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12,
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10
            }}>
              <h5 style={{ margin: 0, fontSize: '.82rem', fontWeight: 700, color: '#334155' }}>
                O que acontecerá a seguir:
              </h5>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: '.8rem', color: '#475569', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <li>
                  <strong>Desativação imediata:</strong> O celular antigo não receberá mais solicitações e a biometria cadastrada nele será inativada no sistema.
                </li>
                <li>
                  <strong>Pareamento Novo:</strong> Um novo código QR será gerado na tela para você conectar outro aparelho celular com o aplicativo instalado.
                </li>
                <li>
                  <strong>Segurança temporária:</strong> Até concluir o novo pareamento, sua conta dependerá apenas da senha usual de login.
                </li>
              </ul>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  // ── Phase: passkey_registering ─────────────────────────────────────────────
  if (phase === 'passkey_registering') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
        padding: '32px 20px', textAlign: 'center',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%',
          background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'sw-success-pulse 2s ease-in-out infinite',
        }}>
          <Fingerprint size={40} style={{ color: '#3b82f6' }} />
        </div>
        <div>
          <p style={{ color: '#0f172a', fontWeight: 800, fontSize: '1.1rem', margin: '0 0 6px' }}>Registrando Passkey...</p>
          <p style={{ color: '#64748b', fontSize: '.85rem', margin: 0, lineHeight: 1.5 }}>
            Confirme sua identidade na janela de segurança do sistema.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: '.8rem', fontWeight: 500 }}>
          <Loader2 size={16} style={{ animation: 'sw-spin .8s linear infinite' }} />
          Aguardando biometria do dispositivo...
        </div>

        <style>{`
          @keyframes sw-success-pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}</style>
      </div>
    );
  }

  // ── Phase: push_permission ─────────────────────────────────────────────
  if (phase === 'push_permission') {
    return (
      <div style={{ minWidth: 340, display: 'flex', justifyContent: 'center' }}>
        <style>{`
          .animate-fade-in-up {
            animation: push-fade-in-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          @keyframes push-fade-in-up {
            from { opacity: 0; transform: translateY(15px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <PushPermissionRequest
          sessionId={sessionId}
          onSuccess={() => startFinalCountdown()}
          onSkip={() => startFinalCountdown()}
        />
      </div>
    );
  }

  // ── Phase: passkey_done ────────────────────────────────────────────────────
  if (phase === 'passkey_done') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
        width: '100%', maxWidth: 340, boxSizing: 'border-box',
        padding: '24px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden',
        borderRadius: 20, background: '#ffffff',
        border: '1px solid rgba(108, 92, 231, 0.25)',
        boxShadow: '0 18px 40px rgba(15, 23, 42, 0.16)',
      }}>
        {/* Triple glow — purple ambient */}
        <div style={{
          position: 'absolute', top: -50, left: '50%', transform: 'translateX(-50%)',
          width: 240, height: 240, background: 'rgba(108, 92, 231, 0.15)',
          filter: 'blur(60px)', zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
          {/* Large animated check */}
          <div style={{
            width: 100, height: 100, borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(108, 92, 231, 0.25), rgba(90, 79, 207, 0.15))',
            border: '3px solid rgba(108, 92, 231, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 40px rgba(108, 92, 231, 0.3), inset 0 0 20px rgba(108, 92, 231, 0.1)',
            animation: 'sw-done-bounce .6s cubic-bezier(.175,.885,.32,1.275), sw-success-pulse 2s ease-in-out .6s infinite',
          }}>
            <CheckCircle2 size={52} strokeWidth={1.5} style={{ color: '#6c5ce7' }} />
          </div>

          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 4px', letterSpacing: '-0.02em' }}>
              Autenticação Concluída!
            </h2>
            <p style={{ color: '#6c5ce7', fontWeight: 600, fontSize: '.85rem', margin: '0 0 6px' }}>
              Estação de Trabalho Registrada com Sucesso
            </p>
            <p style={{ color: '#475569', fontSize: '.75rem', margin: 0, maxWidth: 230, lineHeight: 1.5 }}>
              Este computador agora é uma estação segura com nível máximo de confiança.
            </p>
          </div>

          <button
            onClick={() => {
              if (hasNavigatedRef.current) return;
              hasNavigatedRef.current = true;
              if (doneTimerRef.current) {
                clearInterval(doneTimerRef.current);
                doneTimerRef.current = null;
              }
              onVinculado();
            }}
            style={{
              marginTop: 8, padding: '12px 32px', borderRadius: 14, border: 'none',
              background: 'linear-gradient(135deg, #6c5ce7, #5a4fcf)',
              color: '#fff', fontSize: '.92rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10,
              boxShadow: '0 4px 14px rgba(108, 92, 231, 0.25)',
              transition: 'all .3s cubic-bezier(.16,1,.3,1)',
              position: 'relative', overflow: 'hidden', minWidth: 190,
              justifyContent: 'center',
            }}
          >
            {/* Countdown progress bar overlay */}
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0,
              width: `${((5 - doneCountdown) / 5) * 100}%`,
              background: 'rgba(255,255,255,0.15)',
              transition: 'width 1s linear',
              borderRadius: 14,
            }} />
            <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              CONTINUAR
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 24, height: 24, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)', fontSize: '.8rem', fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
              }}>
                {doneCountdown}
              </span>
            </span>
          </button>
        </div>

        <style>{`
          @keyframes sw-done-bounce {
            0% { transform: scale(0); opacity: 0; }
            60% { transform: scale(1.15); opacity: 1; }
            100% { transform: scale(1); }
          }
          @keyframes sw-success-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 40px rgba(108, 92, 231, 0.3), inset 0 0 20px rgba(108, 92, 231, 0.1); }
            50% { transform: scale(1.04); box-shadow: 0 0 50px rgba(108, 92, 231, 0.4), inset 0 0 25px rgba(108, 92, 231, 0.15); }
          }
        `}</style>
      </div>
    );
  }

  // ── Phase: qr (padrão) ────────────────────────────────────────────────────
  const isExpiring = countdown <= EXPIRY_WARN_S && countdown > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

      {/* Flip: QR ↔ instrução */}
      <div style={{ perspective: 800, width: QR_SIZE, height: QR_SIZE }}>
        <div style={{ position: 'relative', width: '100%', height: '100%', transformStyle: 'preserve-3d', transition: 'transform .6s cubic-bezier(.4,0,.2,1)', transform: flipSide === 'info' ? 'rotateY(180deg)' : 'rotateY(0deg)' }}>

          {/* Frente: QR real */}
          <div style={{
            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
            borderRadius: 18, overflow: 'hidden',
            border: '2px solid rgba(255,255,255,.12)',
            background: '#ffffff',
            boxShadow: '0 4px 32px rgba(0,0,0,.4)',
            animation: isExpiring ? 'sw-expire-pulse 1s ease-in-out infinite' : 'none',
          }}>
            {qrDataUrl && (
              <StyledQrCode
                value={qrDataUrl}
                size={QR_SIZE}
                width={QR_SIZE}
                height={QR_SIZE}
                shape="square"
                style="rounded"
                icon="logo-shield-qrcode-backing"
                iconKind="preset"
                iconSize={150}
                hideBackgroundDots={false}
                foreground="#7c3aed"
                background="#ffffff"
                margin={0}
              />
            )}
            {/* Scan line — vermelho pulsante com blur */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              height: 3,
              background: `linear-gradient(90deg, transparent 5%, ${SCAN_RED} 50%, transparent 95%)`,
              boxShadow: `0 0 12px ${SCAN_RED}, 0 0 24px ${SCAN_RED}60`,
              animation: 'sw-scan 2.5s ease-in-out infinite',
              opacity: 0.85,
              zIndex: 5,
            }} />
          </div>

          {/* Verso: instrução (aparece só na primeira vez, depois nunca mais) */}
          <div style={{
            position: 'absolute', inset: 0, backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)', borderRadius: 18,
            background: `${AMBER}0F`, border: `2px solid ${AMBER}4D`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 14, padding: 24,
          }}>
            <Smartphone size={40} style={{ color: AMBER, opacity: .9 }} />
            <div style={{ textAlign: 'center', fontSize: '.85rem', color: '#e2e8f0', lineHeight: 1.7 }}>
              Abra a câmera do celular e aponte para o QR Code para vincular seu dispositivo.
            </div>
          </div>
        </div>
      </div>

      {/* Countdown */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: isExpiring ? SCAN_RED : '#10b981', animation: 'sw-pulse 1.5s ease-in-out infinite' }} />
        <span style={{ fontSize: '.72rem', color: isExpiring ? '#f87171' : '#64748b', transition: 'color .3s' }}>
          Código válido por <strong style={{ color: isExpiring ? '#fca5a5' : '#94a3b8' }}>{fmtTime(countdown)}</strong>
        </span>
        <button onClick={gerarQr} title="Renovar QR" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 2, display: 'flex', alignItems: 'center' }}>
          <RefreshCw size={11} />
        </button>
      </div>

      {/* DEV: teste sem celular removido por instrução de limpeza */}

      <style>{`
        @keyframes sw-scan {
          0%, 100% { top: 0%; }
          50% { top: calc(100% - 3px); }
        }
      `}</style>
    </div>
  );
}
